import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { KeyRound, RotateCcw, UserCheck, UserPlus, UserX } from 'lucide-react';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { useCapabilitiesStore } from '../../stores/capabilitiesStore';
import { useAuthStore } from '../../stores/authStore';
import { Permission } from '../../auth/permissions';
import type { Role } from '../../auth/roles';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';
import { DataGrid, type Column } from '../../components/ui/DataGrid';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { usePrompt } from '../../components/ui/PromptDialog';
import { PermissionsDrawer } from '../../components/admin/PermissionsDrawer';
import { TempPasswordPanel } from '../../components/TempPasswordPanel';

interface StaffUser {
  id: string;
  email: string;
  role: Role;
  full_name: string | null;
  active: boolean;
  must_change_password: boolean;
  created_at: string;
  /** Printed on invoices, BLs and tickets as the seller; shown in the audit log (4.2). */
  seller_code?: string | null;
}

/** Role code → display label (adminScreens.users.roles.*); unknown codes fall back to the code. */
const roleLabel = (t: TFunction, role: string): string =>
  t(`adminScreens.users.roles.${role}`, { defaultValue: role });
const cid = (): string => crypto.randomUUID();

/**
 * Screens 41-42 — internal user & role management (/admin/users), for OWNER/ADMIN/MANAGER.
 * List, create, change role, and (de)activate staff accounts. The set of roles the current
 * user may create/assign is fetched from the backend (/admin/users/assignable-roles) so the
 * UI never offers a role the API would reject — enforcement stays server-side regardless.
 */
export default function Users() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const [promptCode, promptDialog] = usePrompt();
  const perms = useCapabilitiesStore((s) => s.permissionSet);
  const meId = useAuthStore((s) => s.user?.sub);
  const canManage = perms.has(Permission.STAFF_USER_MANAGE);

  const [rows, setRows] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [assignable, setAssignable] = useState<Role[]>([]);

  // WP-9 — the per-account override / acknowledgment editor.
  const isOwner = useCapabilitiesStore((s) => s.data?.role) === 'OWNER';
  const [permsFor, setPermsFor] = useState<StaffUser | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role | ''>('');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<{
    email: string;
    password: string;
    emailSent?: boolean;
    emailError?: string;
  } | null>(null);

  // Staff headcount is small and bounded, so we fetch everyone once and filter in the client:
  // toggling is instant, and — more importantly — we can always tell the operator how many
  // deactivated accounts are being hidden. Refetching per toggle could not do that: with nothing
  // deactivated the two responses are identical and the checkbox looks broken.
  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<{ data: StaffUser[] }>(
        '/admin/users?includeInactive=true'
      );
      setRows(data.data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const inactiveCount = useMemo(() => rows.filter((u) => !u.active).length, [rows]);
  const visibleRows = useMemo(
    () => (includeInactive ? rows : rows.filter((u) => u.active)),
    [rows, includeInactive]
  );

  useEffect(() => {
    if (!canManage) return;
    apiClient
      .get<{ data: Role[] }>('/admin/users/assignable-roles')
      .then(({ data }) => setAssignable(data.data))
      .catch(() => undefined);
  }, [canManage]);

  const create = async (): Promise<void> => {
    if (!email || !role) {
      push({ tone: 'warning', message: t('adminScreens.users.emailRoleRequired') });
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post<{
        data: StaffUser;
        tempPassword?: string;
        emailSent?: boolean;
        emailError?: string;
      }>(
        '/admin/users',
        { email, role, fullName: fullName || undefined },
        { headers: { 'X-Command-Id': cid() } }
      );
      if (data.tempPassword) {
        // Not a toast: a password has to outlive a glance (see TempPasswordPanel).
        setIssued({
          email: data.data.email,
          password: data.tempPassword,
          emailSent: data.emailSent,
          emailError: data.emailError,
        });
      }
      push({ tone: 'success', message: t('adminScreens.users.created') });
      setEmail('');
      setFullName('');
      setRole('');
      setShowCreate(false);
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const changeRole = async (u: StaffUser, newRole: Role): Promise<void> => {
    if (newRole === u.role) return;
    try {
      await apiClient.patch(
        `/admin/users/${u.id}/role`,
        { role: newRole },
        { headers: { 'X-Command-Id': cid() } }
      );
      push({ tone: 'success', message: `${u.email} → ${roleLabel(t, newRole)}` });
      await load();
    } catch (err) {
      showApiError(err);
    }
  };

  const setActive = async (u: StaffUser, active: boolean): Promise<void> => {
    try {
      await apiClient.patch(
        `/admin/users/${u.id}/active`,
        { active },
        { headers: { 'X-Command-Id': cid() } }
      );
      push({
        tone: 'success',
        message: active
          ? t('adminScreens.users.reactivated', { email: u.email })
          : t('adminScreens.users.deactivated', { email: u.email }),
      });
      await load();
    } catch (err) {
      showApiError(err);
    }
  };

  const editSellerCode = async (u: StaffUser): Promise<void> => {
    const code = await promptCode({
      title: t('adminScreens.users.sellerCode.title', { name: u.full_name || u.email }),
      description: t('adminScreens.users.sellerCode.description'),
      label: t('adminScreens.users.columns.sellerCode'),
      initialValue: u.seller_code ?? '',
      mono: true,
      minLength: 2,
      validate: (v) =>
        /^[A-Za-z0-9]{2,8}$/.test(v.trim()) ? null : t('adminScreens.users.sellerCode.invalid'),
    });
    if (!code) return;
    try {
      await apiClient.patch(
        `/admin/users/${u.id}/seller-code`,
        { sellerCode: code.trim().toUpperCase() },
        { headers: { 'X-Command-Id': cid() } }
      );
      await load();
    } catch (err) {
      showApiError(err);
    }
  };

  const resetPassword = async (u: StaffUser): Promise<void> => {
    // The current password stops working the moment this runs, so it needs an answer first.
    const ok = await confirm({
      title: t('adminScreens.users.reset.title'),
      description: t('adminScreens.users.reset.description', { email: u.email }),
      confirmLabel: t('adminScreens.users.reset.confirm'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      const { data } = await apiClient.post<{
        data: { tempPassword: string; emailSent?: boolean; emailError?: string };
      }>(`/admin/users/${u.id}/reset-password`, {}, { headers: { 'X-Command-Id': cid() } });
      setIssued({
        email: u.email,
        password: data.data.tempPassword,
        emailSent: data.data.emailSent,
        emailError: data.data.emailError,
      });
    } catch (err) {
      showApiError(err);
    }
  };

  const columns = useMemo<Column<StaffUser>[]>(() => {
    const base: Column<StaffUser>[] = [
      {
        key: 'email',
        header: t('adminScreens.users.columns.user'),
        render: (u) => (
          <div className="min-w-0">
            <div className="truncate font-medium">{u.full_name || u.email}</div>
            {u.full_name && <div className="truncate text-xs text-muted-foreground">{u.email}</div>}
          </div>
        ),
      },
      {
        key: 'seller_code',
        header: t('adminScreens.users.columns.sellerCode'),
        render: (u) => (
          <div className="flex items-center gap-1.5">
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs font-semibold">
              {u.seller_code ?? '—'}
            </span>
            {canManage && (
              <button
                type="button"
                className="text-xs text-muted-foreground underline hover:text-foreground"
                onClick={() => void editSellerCode(u)}
              >
                {t('adminScreens.users.sellerCode.edit')}
              </button>
            )}
          </div>
        ),
      },
      {
        key: 'role',
        header: t('adminScreens.users.columns.role'),
        render: (u) =>
          canManage && u.id !== meId && (assignable.includes(u.role) || assignable.length > 0) ? (
            <Select
              className="h-8 w-44"
              value={u.role}
              onChange={(e) => changeRole(u, e.target.value as Role)}
            >
              {/* current role always selectable even if not in the assignable set (read-only display) */}
              {!assignable.includes(u.role) && (
                <option value={u.role}>{roleLabel(t, u.role)}</option>
              )}
              {assignable.map((r) => (
                <option key={r} value={r}>
                  {roleLabel(t, r)}
                </option>
              ))}
            </Select>
          ) : (
            <Badge variant="secondary">{roleLabel(t, u.role)}</Badge>
          ),
      },
      {
        key: 'active',
        header: t('adminScreens.users.columns.status'),
        render: (u) => (
          <div className="flex items-center gap-1.5">
            <Badge variant={u.active ? 'success' : 'muted'}>
              {u.active ? t('adminScreens.users.active') : t('adminScreens.users.inactive')}
            </Badge>
            {u.must_change_password && (
              <Badge variant="warning" title={t('adminScreens.users.pwdPendingTitle')}>
                {t('adminScreens.users.pwdPending')}
              </Badge>
            )}
          </div>
        ),
      },
    ];
    if (!canManage) return base;
    base.push({
      key: 'actions',
      header: '',
      sortable: false,
      noTruncate: true,
      render: (u) =>
        u.id === meId ? (
          <div className="flex justify-end">
            <span className="text-xs text-muted-foreground">{t('adminScreens.users.you')}</span>
          </div>
        ) : (
          // Segmented action group: one bordered control per row instead of three
          // competing buttons — the destructive action stays tinted, not filled.
          <div className="flex justify-end">
            <div className="inline-flex divide-x divide-border overflow-hidden rounded-md border border-input">
              <Button
                size="sm"
                variant="ghost"
                className="gap-1.5 rounded-none px-2.5 font-normal text-muted-foreground hover:text-foreground"
                title={t('adminScreens.users.actions.permissionsTitle')}
                onClick={() => setPermsFor(u)}
              >
                <KeyRound className="h-3.5 w-3.5" />
                {t('adminScreens.users.actions.permissions')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="gap-1.5 rounded-none px-2.5 font-normal text-muted-foreground hover:text-foreground"
                title={t('adminScreens.users.actions.resetTitle')}
                onClick={() => resetPassword(u)}
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t('adminScreens.users.actions.reset')}
              </Button>
              {u.active ? (
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 rounded-none px-2.5 font-normal text-destructive hover:bg-destructive/10 hover:text-destructive"
                  title={t('adminScreens.users.actions.deactivateTitle')}
                  onClick={() => setActive(u, false)}
                >
                  <UserX className="h-3.5 w-3.5" />
                  {t('adminScreens.users.actions.deactivate')}
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5 rounded-none px-2.5 font-normal text-success hover:bg-success/10 hover:text-success"
                  title={t('adminScreens.users.actions.reactivateTitle')}
                  onClick={() => setActive(u, true)}
                >
                  <UserCheck className="h-3.5 w-3.5" />
                  {t('adminScreens.users.actions.reactivate')}
                </Button>
              )}
            </div>
          </div>
        ),
    });
    return base;
  }, [canManage, assignable, meId, t]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!perms.has(Permission.STAFF_USER_VIEW)) {
    return <div className="p-6 text-muted-foreground">{t('adminScreens.users.noPermission')}</div>;
  }

  return (
    <div>
      <PageHeader
        title={t('adminScreens.users.title')}
        description={t('adminScreens.users.description')}
        actions={
          <div className="flex items-center gap-3">
            <label
              className={`flex items-center gap-2 text-sm ${
                inactiveCount === 0
                  ? 'cursor-default text-muted-foreground/60'
                  : 'cursor-pointer text-muted-foreground hover:text-foreground'
              }`}
              title={
                inactiveCount === 0
                  ? t('adminScreens.users.allActive')
                  : t('adminScreens.users.hiddenInactive', { count: inactiveCount })
              }
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input accent-primary disabled:opacity-50"
                checked={includeInactive}
                disabled={inactiveCount === 0}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              {t('adminScreens.users.showInactive')}
              {inactiveCount > 0 && (
                <Badge variant="muted" className="px-1.5">
                  {inactiveCount}
                </Badge>
              )}
            </label>
            {canManage && (
              <Button size="sm" onClick={() => setShowCreate((v) => !v)}>
                <UserPlus className="h-4 w-4" />
                {t('adminScreens.users.newUser')}
              </Button>
            )}
          </div>
        }
      />
      {issued && (
        <TempPasswordPanel
          email={issued.email}
          password={issued.password}
          emailSent={issued.emailSent}
          emailError={issued.emailError}
          audience="staff"
          onDismiss={() => setIssued(null)}
        />
      )}

      {canManage && showCreate && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">{t('adminScreens.users.create.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-email">{t('adminScreens.users.create.email')}</Label>
                <Input
                  id="new-email"
                  type="email"
                  placeholder="jean@mecadepot.fr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-name">{t('adminScreens.users.create.fullName')}</Label>
                <Input
                  id="new-name"
                  placeholder="Jean Dupont"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-role">{t('adminScreens.users.create.role')}</Label>
                <Select
                  id="new-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value as Role)}
                >
                  <option value="" disabled>
                    {t('adminScreens.users.create.selectRole')}
                  </option>
                  {assignable.map((r) => (
                    <option key={r} value={r}>
                      {roleLabel(t, r)}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex items-end">
                <Button className="w-full" onClick={create} disabled={busy}>
                  <UserPlus className="h-4 w-4" />
                  {busy
                    ? t('adminScreens.users.create.creating')
                    : t('adminScreens.users.create.submit')}
                </Button>
              </div>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              {t('adminScreens.users.create.hint')}
            </p>
          </CardContent>
        </Card>
      )}

      <DataGrid
        columns={columns}
        rows={visibleRows}
        loading={loading}
        rowKey={(u) => u.id}
        empty={
          includeInactive ? t('adminScreens.users.empty') : t('adminScreens.users.emptyActive')
        }
      />

      {permsFor && (
        <PermissionsDrawer
          userId={permsFor.id}
          userLabel={permsFor.full_name || permsFor.email}
          isOwner={isOwner}
          onClose={() => setPermsFor(null)}
        />
      )}

      {confirmDialog}
      {promptDialog}
    </div>
  );
}
